import { createContext } from 'react';

import type { EventContent } from '@shared/events';

/**
 * Everything about the current track *except* live playback position --
 * metadata, transport controls, ready/playing state. Deliberately excludes
 * `playedSeconds`/`loadedSeconds`/`duration` (see
 * `AudioPlayerProgressContext`): those change several times a second while
 * playing, and every consumer of a context re-renders whenever its value's
 * identity changes, regardless of which fields it actually reads. Keeping
 * them in this context meant `MuteBtn`/`CloseBtn`/`PlayPauseBtn`/the skip
 * buttons and `CollapsiblePlayer`'s own wrapper -- none of which read
 * position -- all re-rendered on every tick along with the progress bar.
 * Split 2026-09-17 once that was confirmed against the actual consumer list.
 *
 * Carries `getPlayedSeconds` (a stable function reading a ref, same pattern
 * as `getHandle` in `audio-player.tsx`) for the skip buttons specifically:
 * they need the *current* position only at click time, to compute a
 * relative seek target, never to render anything -- subscribing to
 * `AudioPlayerProgressContext` for that would tick-re-render them for no
 * reason, exactly what splitting the context in the first place was for.
 */
export const AudioPlayerContext = createContext<AudioPlayerContextProps>({
	url: null,
	isReady: false,
	isPlaying: false,
	setIsReady: () => {},
	artwork: undefined,
	setArtwork: () => {},
	title: undefined,
	setTitle: () => {},
	setAlbum: () => {},
	setSubject: () => {},
	play: () => {},
	pause: () => {},
	close: () => {},
	playPause: () => {},
	toggleMute: () => {},
	muted: false,
	setMuted: () => {},
	onProgressChange: () => {},
	getPlayedSeconds: () => 0,
});

interface AudioPlayerContextProps {
	url: string | null;
	isReady: boolean;
	setIsReady: (isReady: boolean) => void;
	artwork?: Array<MediaImage>;
	setArtwork: (value: Array<MediaImage> | undefined) => void;
	title?: string;
	setTitle: (value: string | undefined) => void;
	album?: string;
	setAlbum: (value: string | undefined) => void;
	/**
	 * The issue and article the track belongs to, for the audio events. Set
	 * by whoever hands the player a track: the player keeps playing while
	 * the reader moves on, so it cannot take them from what is on screen.
	 */
	setSubject: (subject: EventContent | undefined) => void;
	isPlaying: boolean;
	play: () => void;
	pause: () => void;
	close: () => void;
	playPause: () => void;
	toggleMute: () => void;
	muted: boolean;
	setMuted: (muted: boolean) => void;
	onProgressChange: (progress: number, type: 'seconds' | 'fraction') => void;
	getPlayedSeconds: () => number;
}

/**
 * Live playback position: written on every engine `timeupdate` tick (several
 * times a second while playing). Split out from `AudioPlayerContext` (see its
 * own comment) so only components that actually render position --
 * `ProgressBar`'s slider/time text, and the skip buttons, which read
 * `playedSeconds` to compute their seek target -- re-render on every tick.
 *
 * No setters here: `onProgressChange` (on `AudioPlayerContext`) is the only
 * way a consumer changes position -- it updates this context's values
 * itself, only once a seek actually happens (see its own JSDoc in
 * `audio-player.tsx`), so a consumer that wrote here directly could show a
 * position the audio was never actually at.
 *
 * Carries `duration` rather than a 0-100 `progress` percentage or a 0-1
 * `loaded` fraction: both of those were previously separate state, always
 * set in the same call alongside `playedSeconds`/`loadedSeconds` from the
 * same total and never independently -- pure, redundant functions of
 * `playedSeconds`/`loadedSeconds`/`duration`, costing a second `setState`
 * per tick for no information gain. `ProgressBar` (the one consumer that
 * needs a percentage) computes it locally now, `duration / (loadedSeconds *
 * duration)`-style math elsewhere is gone. Exposing the real `duration`
 * also let `ProgressBar` drop its own reconstruction of it from
 * `loadedSeconds`/`loaded` -- a workaround for not having this field at
 * all, not something worth keeping once it's this cheap to add.
 */
export const AudioPlayerProgressContext =
	createContext<AudioPlayerProgressContextProps>({
		playedSeconds: 0,
		loadedSeconds: 0,
		duration: 0,
	});

interface AudioPlayerProgressContextProps {
	playedSeconds: number;
	loadedSeconds: number;
	duration: number;
}
